import styles from "./SectionTable.module.scss";
import classNames from "classnames/bind";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import { useState, useEffect, useRef } from "react";
import React from "react";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { PlusIcon } from "lucide-react";
import { IOutputProduct } from "~/utils/interface/jobDetails";
import { DragIcon } from "~/assets/icons";

const cx = classNames.bind(styles);

interface LineItem {
  id: string;
  itemNumber?: string;
  itemName: string;
  description: string;
  quantity: number;
  uom: string;
  type: "Material" | "Labor";
  markup: number;
  price: number;
}

interface SectionTableProps {
  showTable: boolean;
  onDataChange?: (data: { formData: LineItem[]; sectionTotal: number }) => void;
  listProductInvoice?: IOutputProduct[];
  initialLines?: LineItem[];
}

const SectionTable = ({
  showTable,
  onDataChange,
  listProductInvoice = [],
  initialLines,
}: SectionTableProps) => {
  const [formData, setFormData] = useState<LineItem[]>(
    initialLines && initialLines.length > 0
      ? initialLines
      : [
          {
            id: "1",
            itemName: "",
            description: "",
            quantity: 0,
            uom: "SQ",
            type: "Material",
            markup: 0,
            price: 0,
          },
        ]
  );

  // Sync formData when initialLines changes
  useEffect(() => {
    if (initialLines && initialLines.length > 0) {
      setFormData(initialLines);
    }
  }, [initialLines]);

  // State for autocomplete dropdown
  const [activeAutocompleteId, setActiveAutocompleteId] = useState<
    string | null
  >(null);
  const [filteredProducts, setFilteredProducts] = useState<IOutputProduct[]>(
    []
  );
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const autocompleteRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);

  const handleAddLineItem = () => {
    const newLineItem: LineItem = {
      id: Date.now().toString(),
      itemName: "",
      description: "",
      quantity: 0,
      uom: "SQ",
      type: "Material",
      markup: 0,
      price: 0,
    };

    setFormData([...formData, newLineItem]);
  };

  const handleLineItemChange = (
    id: string,
    field: keyof LineItem,
    value: any
  ) => {
    setFormData(
      formData.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const calculateDropdownPosition = (id: string) => {
    const wrapperElement = autocompleteRefs.current[id];

    if (wrapperElement) {
      const inputElement = wrapperElement.querySelector(
        "input"
      ) as HTMLInputElement;

      if (inputElement) {
        const rect = inputElement.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const dropdownMinWidth = 400;
        const dropdownMaxWidth = 650;
        const dropdownMaxHeight = 400;
        const spacing = 4;
        const padding = 8;

        const dropdownWidth = Math.min(
          dropdownMaxWidth,
          Math.max(dropdownMinWidth, rect.width)
        );

        let left = rect.left;

        if (left + dropdownWidth > viewportWidth - padding) {
          left = Math.max(padding, viewportWidth - dropdownWidth - padding);
        }

        if (left < padding) {
          left = padding;
        }

        const spaceBelow = viewportHeight - rect.bottom - spacing - padding;
        const spaceAbove = rect.top - spacing - padding;

        let top: number;

        if (spaceBelow < 200 && spaceAbove > spaceBelow) {
          const estimatedHeight = Math.min(dropdownMaxHeight, spaceAbove);
          top = rect.top - estimatedHeight - spacing;

          if (top < padding) {
            top = padding;
          }
        } else {
          top = rect.bottom + spacing;

          const maxAvailableHeight = spaceBelow;
          if (maxAvailableHeight < 100) {
            const estimatedHeight = Math.min(dropdownMaxHeight, spaceAbove);
            top = Math.max(padding, rect.top - estimatedHeight - spacing);
          }
        }

        return {
          top,
          left,
        };
      }
    }
    return null;
  };

  useEffect(() => {
    if (activeAutocompleteId && filteredProducts.length > 0) {
      requestAnimationFrame(() => {
        const position = calculateDropdownPosition(activeAutocompleteId);
        if (position) {
          setDropdownPosition(position);
        }
      });
    } else {
      setDropdownPosition(null);
    }
  }, [activeAutocompleteId, filteredProducts]);

  const handleItemNameChange = (id: string, value: string) => {
    handleLineItemChange(id, "itemName", value);

    if (value.trim() && listProductInvoice.length > 0) {
      const filtered = listProductInvoice.filter((product) => {
        const searchTerm = value.toLowerCase();
        return (
          product.itemName?.toLowerCase().includes(searchTerm) ||
          product.itemNumber?.toLowerCase().includes(searchTerm) ||
          product.description?.toLowerCase().includes(searchTerm)
        );
      });
      setFilteredProducts(filtered);
      setActiveAutocompleteId(id);
    } else {
      setFilteredProducts([]);
      setActiveAutocompleteId(null);
      setDropdownPosition(null);
    }
  };

  const handleProductSelect = (id: string, product: IOutputProduct) => {
    const currentItem = formData.find((item) => item.id === id);
    const updatedItem: Partial<LineItem> = {
      itemName: product.itemName || "",
      description: product.description || "",
      uom: product.uom || currentItem?.uom || "SQ",
      price:
        product.price !== undefined && product.price !== null
          ? product.price
          : currentItem?.price || 0,
      markup:
        product.markup !== undefined && product.markup !== null
          ? product.markup
          : currentItem?.markup || 0,
      quantity:
        product.quantity !== undefined && product.quantity !== null
          ? product.quantity
          : currentItem?.quantity || 0,
      itemNumber: product.itemNumber || "",
      type:
        (product.type as "Material" | "Labor") ||
        currentItem?.type ||
        "Material",
    };

    setFormData(
      formData.map((item) =>
        item.id === id ? { ...item, ...updatedItem } : item
      )
    );

    setActiveAutocompleteId(null);
    setFilteredProducts([]);
    setDropdownPosition(null);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeAutocompleteId) {
        const wrapperRef = autocompleteRefs.current[activeAutocompleteId];
        const target = event.target as Node;

        if (wrapperRef && !wrapperRef.contains(target)) {
          const dropdown = document.querySelector(
            "[data-autocomplete-dropdown]"
          );
          if (!dropdown || !dropdown.contains(target)) {
            setActiveAutocompleteId(null);
            setFilteredProducts([]);
            setDropdownPosition(null);
          }
        }
      }
    };

    const handleWheel = (event: WheelEvent) => {
      if (activeAutocompleteId) {
        const target = event.target as Node;
        const dropdown = document.querySelector("[data-autocomplete-dropdown]");
        const modalContainer = document.querySelector(
          ".create-invoice-container .content"
        );

        const isWheelInsideDropdown =
          dropdown && (dropdown === target || dropdown.contains(target));

        const isWheelInsideModal =
          modalContainer &&
          (modalContainer === target || modalContainer.contains(target));

        if (!isWheelInsideDropdown && !isWheelInsideModal) {
          setActiveAutocompleteId(null);
          setFilteredProducts([]);
          setDropdownPosition(null);
        }
      }
    };

    const handleResize = () => {
      if (activeAutocompleteId && filteredProducts.length > 0) {
        requestAnimationFrame(() => {
          const position = calculateDropdownPosition(activeAutocompleteId);
          if (position) {
            setDropdownPosition(position);
          } else {
            setActiveAutocompleteId(null);
            setFilteredProducts([]);
            setDropdownPosition(null);
          }
        });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("wheel", handleWheel, {
      passive: true,
      capture: true,
    });
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("wheel", handleWheel, { capture: true });
      window.removeEventListener("resize", handleResize);
    };
  }, [activeAutocompleteId, filteredProducts.length]);

  const calculateLineTotal = (item: LineItem) => {
    const quantity = parseFloat(item.quantity.toString()) || 0;
    const price = parseFloat(item.price.toString()) || 0;
    const markup = parseFloat(item.markup.toString()) || 0;
    const total = quantity * price + (quantity * price * markup) / 100;
    return total.toFixed(2);
  };

  const calculateSectionTotal = (): number => {
    return formData.reduce((total, item) => {
      const quantity = parseFloat(item.quantity.toString()) || 0;
      const price = parseFloat(item.price.toString()) || 0;
      const markup = parseFloat(item.markup.toString()) || 0;
      const lineTotal = quantity * price + (quantity * price * markup) / 100;
      return total + lineTotal;
    }, 0);
  };

  const handleDeleteLineItem = (id: string) => {
    setFormData(formData.filter((item) => item.id !== id));
  };

  // Drag and drop handlers - move item immediately on dragOver
  const handleDragStart = (e: React.DragEvent, item: LineItem) => {
    setDraggedItemId(item.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", "");
  };

  const handleDragOver = (e: React.DragEvent, item: LineItem) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    if (draggedItemId && item.id !== draggedItemId) {
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseY = e.clientY;
      const itemMiddle = rect.top + rect.height / 2;

      const currentIndex = formData.findIndex((i) => i.id === draggedItemId);
      const targetIndex = formData.findIndex((i) => i.id === item.id);

      let insertIndex: number;

      if (mouseY < itemMiddle) {
        insertIndex = targetIndex;
      } else {
        insertIndex = targetIndex + 1;
      }

      // Adjust insertIndex if dragging from before target
      if (currentIndex < insertIndex) {
        insertIndex = insertIndex - 1;
      }

      // Only update if position actually changed
      if (currentIndex !== insertIndex && insertIndex >= 0) {
        const newItems = [...formData];
        const draggedItem = newItems[currentIndex];
        newItems.splice(currentIndex, 1);
        newItems.splice(insertIndex, 0, draggedItem);
        setFormData(newItems);
      }
    }
  };

  const handleDragLeave = () => {
    // No-op: item moves immediately on dragOver, no need to track leave
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggedItemId(null);
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
  };

  // Expose formData and sectionTotal to parent component
  useEffect(() => {
    if (onDataChange) {
      const sectionTotal = calculateSectionTotal();
      onDataChange({ formData, sectionTotal });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  return showTable ? (
    <div className={cx("section-table")}>
      <div className={cx("table-wrapper")}>
        <div className={cx("table-header")}>
          <div className={cx("table-cell", "drag-cell")}></div>
          <div className={cx("table-cell", "item-name")}>Item Name</div>
          <div className={cx("table-cell", "description")}>Description</div>
          <div className={cx("table-cell", "quantity")}>Quantity</div>
          <div className={cx("table-cell", "ml")}>M/L</div>
          <div className={cx("table-cell", "makeup")}>Makeup%</div>
          <div className={cx("table-cell", "price")}>Price</div>
          <div className={cx("table-cell", "amount")}>Amount</div>
          <div className={cx("table-cell", "action")}></div>
        </div>

        <div className={cx("table-body")}>
          {formData.length > 0 ? (
            <>
              {formData.map((item, index) => (
                <div
                  key={item.id}
                    className={cx("table-row", {
                      "is-dragging": draggedItemId === item.id,
                    })}
                    draggable
                    onDragStart={(e) => handleDragStart(e, item)}
                    onDragOver={(e) => handleDragOver(e, item)}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                  >
                    <div className={cx("table-cell", "drag-cell")}>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <DragIcon className={cx("drag-icon")} />
                      </button>
                    </div>
                  <div className={cx("table-cell", "item-name")}>
                    <div
                      className={cx("autocomplete-wrapper")}
                      ref={(el) => {
                        autocompleteRefs.current[item.id] = el;
                      }}
                    >
                      <PrimaryInput
                        name={`line-item-name-${item.id}`}
                        placeholder="Item Name"
                        value={item.itemName}
                        onChange={(e) =>
                          handleItemNameChange(item.id, e.target.value)
                        }
                        className={cx("table-input")}
                        autocomplete={false}
                      />
                      {activeAutocompleteId === item.id &&
                        filteredProducts.length > 0 &&
                        dropdownPosition && (
                          <div
                            className={cx("autocomplete-dropdown")}
                            data-autocomplete-dropdown
                            style={{
                              top: `${dropdownPosition.top}px`,
                              left: `${dropdownPosition.left}px`,
                            }}
                          >
                            {filteredProducts.map((product, index) => (
                              <div
                                key={index}
                                className={cx("autocomplete-item")}
                                onClick={() =>
                                  handleProductSelect(item.id, product)
                                }
                              >
                                <div className={cx("autocomplete-item-name")}>
                                  {product.itemName ||
                                    product.itemNumber ||
                                    "Unnamed Product"}
                                </div>
                                {product.itemNumber && product.itemName && (
                                  <div
                                    className={cx("autocomplete-item-number")}
                                  >
                                    #{product.itemNumber}
                                  </div>
                                )}
                                {product.description && (
                                  <div
                                    className={cx(
                                      "autocomplete-item-description"
                                    )}
                                  >
                                    {product.description}
                                  </div>
                                )}
                                <div className={cx("autocomplete-item-footer")}>
                                  {product.price !== undefined &&
                                    product.price !== null && (
                                      <div
                                        className={cx(
                                          "autocomplete-item-price"
                                        )}
                                      >
                                        ${product.price.toFixed(2)}
                                      </div>
                                    )}
                                  {product.uom && (
                                    <div
                                      className={cx("autocomplete-item-uom")}
                                    >
                                      {product.uom}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                  <div className={cx("table-cell", "description")}>
                    <PrimaryInput
                      name={`description-${item.id}`}
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) =>
                        handleLineItemChange(
                          item.id,
                          "description",
                          e.target.value
                        )
                      }
                      className={cx("table-input")}
                    />
                  </div>
                  <div className={cx("table-cell", "quantity")}>
                    <PrimaryInput
                      name={`qty-${item.id}`}
                      placeholder="Quantity"
                      value={item.quantity.toString()}
                      onChange={(e) =>
                        handleLineItemChange(
                          item.id,
                          "quantity",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className={cx("table-input")}
                      type="number"
                    />
                  </div>
                  <div className={cx("table-cell", "ml")}>
                    <p className={cx("cell-value")}>
                      {item.type === "Material" ? "M" : "L"}
                    </p>
                  </div>
                  <div className={cx("table-cell", "makeup")}>
                    <PrimaryInput
                      name={`makeup-${item.id}`}
                      placeholder="Makeup%"
                      value={item.markup?.toString() || "0"}
                      onChange={(e) =>
                        handleLineItemChange(
                          item.id,
                          "markup",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className={cx("table-input")}
                      type="number"
                    />
                  </div>
                  <div className={cx("table-cell", "price")}>
                    <PrimaryInput
                      name={`price-${item.id}`}
                      placeholder="Price"
                      value={item.price.toString()}
                      onChange={(e) =>
                        handleLineItemChange(
                          item.id,
                          "price",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className={cx("table-input")}
                      type="number"
                    />
                  </div>
                  <div className={cx("table-cell", "amount")}>
                    <span className={cx("cell-value")}>
                      ${calculateLineTotal(item)}
                    </span>
                  </div>
                  <div className={cx("table-cell", "action")}>
                    <button
                      className={cx("delete-button")}
                      onClick={() => handleDeleteLineItem(item.id)}
                      type="button"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className={cx("table-row", "no-items")}>
              No items in this section
            </div>
          )}
        </div>
      </div>

      <div className={cx("add-item-button-container")}>
        <PrimaryButton
          title="Add item"
          onClick={() => handleAddLineItem()}
          className={cx("add-item-button")}
          icon={<PlusIcon className={cx("plus-icon")} />}
          iconPosition="left"
        />
      </div>
    </div>
  ) : null;
};

export default SectionTable;
